import type { Messages } from '@ah/i18n';

/** English — optional/secondary locale (ADR-015). Any key missing here falls back to `el`. */
export const en: Messages = {
  nav: {
    findTherapist: 'Find a therapist',
    checkIn: 'Check-in',
    settings: 'Settings',
    signOut: 'Sign out',
    signIn: 'Sign in',
    back: '← Back',
    backHome: 'Home',
  },
  auth: {
    login: {
      title: 'Sign in',
      email: 'Email',
      password: 'Password',
      submit: 'Sign in',
      submitting: 'Signing in…',
      missingFields: 'Please enter your email and password.',
      resendPrompt: "Can't find the confirmation email? We can send it again.",
      resendButton: 'Resend the email',
      resendSent: 'If an account exists for that address, a new link has been sent. Check your spam folder too.',
      resendFailed: 'Something went wrong. Try again shortly.',
      failed: 'Login failed. Please try again.',
      noAccount: 'No account?',
      registerLink: 'Register anonymously',
    },
    twoFactor: {
      prompt: 'Enter the six-digit code from your authenticator app or your email.',
      codeLabel: 'Code',
      submit: 'Confirm',
      failed: 'Incorrect code. Try again.',
      recoveryHint: "If you can't access it, you can enter a recovery code (e.g. ABCDE-12345).",
    },
    register: {
      title: 'Start anonymously',
      intro: "Pick a pseudonym — your therapist will never see your real name unless you choose to reveal it.",
      email: 'Email',
      pseudonym: 'Pseudonym',
      pseudonymPlaceholder: 'e.g. nyx_01',
      password: 'Password (min 10 chars, mixed case + number)',
      submit: 'Create anonymous account',
      submitting: 'Creating your account…',
      failed: 'Registration failed.',
      legalNoticePrefix: 'I have read and accept the',
      legalNoticeJoiner: 'and the',
      mustAcceptTos: 'You must accept the Terms of Use and Privacy Policy to continue.',
      termsLink: 'Terms of Use',
      privacyLink: 'Privacy Policy',
      alreadyRegistered: 'Already registered?',
      loginLink: 'Sign in',
    },
  },
  therapistProfile: {
    back: 'Back to psychologists',
    fallbackName: 'Psychologist',
    loadError: 'Could not load this profile.',
    notFoundHint: 'The link may be out of date, or this therapist may no longer be taking bookings.',
    servicesTitle: 'Services & pricing',
    noServices: 'This therapist has no active services right now.',
    perSession: 'per session',
    bookNeedsAccount: 'Book a session',
    registerHint: 'You are only asked to register when you book. A pseudonym and an email is all it takes.',
    aboutTitle: 'About',
    worksWithTitle: 'Works with',
    videoTitle: 'Intro video',
    videoPrivacy: 'The video is hosted by an outside service. It loads only when you press play.',
    yearsExperience: '{{years}} years of experience',
    licensedIn: 'Licensed in {{country}}',
    translationMissing: 'This profile has not been translated yet — the Greek version is shown.',
  },
  agreementTerms: {
    version: 'Version {{version}}',
    clientLink: 'Terms for clients',
    therapistLink: 'Terms for therapists',
    siteTermsLink: 'Site terms of use',
  },
  verifyEmail: {
    title: 'Confirm your email',
    success: 'Your email is confirmed. You can sign in now.',
    already: 'This email was already confirmed. You can sign in as usual.',
    noToken: 'The link is incomplete. Open it directly from the email.',
    failed: "We couldn't confirm this email.",
    failedHint: 'Request a new link from the sign-in page.',
    goToLogin: 'Go to sign in',
  },
  home: {
    noTherapists: 'No approved therapists in this category yet.',
    loading: 'Loading…',
    allCategories: 'All categories',
    defaultTherapistName: 'Therapist',
    watchIntro: '▶ Watch the intro video',
    fromPricePerSession: 'From {{price}} / session',
    bookSession: 'Book session',
    startToBook: 'Get started to book',
    translationMissing: '(not yet translated to English)',
    speaks: 'Speaks:',
  },
  matching: {
    title: 'Find a therapist',
    specializationPlaceholder: 'Specialization (e.g. anxiety) — optional',
    languagePlaceholder: 'Session language — optional',
    anyLanguage: 'Any language',
    search: 'Search',
    searching: 'Searching…',
    loadError: 'Could not load therapists.',
    noResults: 'No therapists matched. Try a different specialization, or search with none.',
    defaultTherapistName: 'Therapist',
    generalPractice: 'General practice',
    yearsExperience: '{{years}} yrs experience',
    experienceNA: 'Experience n/a',
    bookSession: 'Book a session',
    noBookableSessions: 'No bookable sessions configured yet.',
    translationMissing: '(profile not yet in English — showing Greek)',
    speaks: 'Speaks:',
    viewProfile: 'View profile',
  },
  booking: {
    noBookableSessions: 'This therapist has no active services right now.',
    title: 'Book a session',
    titleWith: 'Book with {{name}}',
    defaultTherapistName: 'this therapist',
    needSignIn: 'You need to be signed in to book a session.',
    signIn: 'Sign in',
    noTherapistData: "We don't have this therapist's session list anymore (this page only works coming from a search result).",
    findTherapist: 'Find a therapist',
    sessionType: 'Session type',
    dateTime: 'Date & time',
    callMode: 'Call mode',
    anonymousMode: 'Anonymous — audio only, voice disguised, no camera',
    standardMode: 'Named — your real voice and, if you choose, video',
    missingFields: 'Please pick a session type and a date/time.',
    createError: 'Could not create the booking. Please try again.',
    submit: 'Confirm booking',
    submitting: 'Booking…',
    almostThere: 'Almost there',
    confirmedBody: "Your session with {{name}} is scheduled for {{when}}. We hold your spot as soon as payment is authorized — you'll only be charged once the therapist accepts. If the therapist doesn't respond within 72 hours, the hold is released automatically and you're not charged.",
    paymentError: 'Could not start payment. Please try again.',
    continueToPayment: 'Continue to payment',
    redirectingToPayment: 'Redirecting to payment…',
    backHome: 'Back home',
  },
  checkIn: {
    title: 'Check in',
    intro: "Write about what's on your mind. This isn't shared with anyone unless you choose to — it's a private, quick check before you book or message a therapist.",
    placeholder: "Type freely — there's no wrong way to say it.",
    ariaLabel: "What's on your mind",
    submit: 'Check in',
    submitting: 'Checking…',
    error: 'Could not check this right now.',
    riskDetected: 'What you wrote sounds like it might be really hard right now. Please consider reaching out to one of the lines below, or to someone you trust.',
    noRisk: "Thanks for checking in. You can continue to booking or messaging whenever you're ready.",
    disclaimer: "This app does not replace professional help. If you're in crisis, please contact:",
    lineKlimaka: 'ΚΛΙΜΑΚΑ — Suicide intervention line:',
    lineMinistry: 'Ministry of Health psychosocial support:',
    lineEmergency: 'Emergency:',
    lineKlimakaSuffix: '(24/7, free, anonymous)',
    lineMinistrySuffix: '(24/7, free)',
    back: 'Back',
  },
  settings: {
    title: 'Settings',
    appearance: 'Appearance',
    language: 'Language',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    deleteAccount: 'Delete account',
    scheduledFor: 'Your account is scheduled for deletion on {{date}}. You can cancel any time before then.',
    cancelling: 'Cancelling…',
    cancelRequest: 'Cancel deletion request',
    graceNotice: 'This starts a {{days}}-day grace period during which you can still cancel. After that, your profile, messages, and login are permanently anonymized. Bookings and invoices are kept only as required by tax law, disconnected from your identity. Enter your password to confirm.',
    password: 'Password',
    submitting: 'Submitting…',
    confirmDeletion: 'Confirm deletion',
    cancel: 'Cancel',
    deleteMyAccount: 'Delete my account',
    deletionRequestError: 'Could not submit the deletion request.',
    deletionCancelError: 'Could not cancel the deletion request.',
    back: '← Back',
  },
  call: {
    connecting: 'Connecting…',
    waitingForOther: 'Waiting for the other participant…',
    ending: 'Ending call…',
    ended: 'Call ended — {{duration}}',
    anonymousNotice: 'Anonymous call — your voice is disguised, camera is off.',
    mute: 'Mute',
    unmute: 'Unmute',
    endCall: 'End call',
    done: 'Done',
    back: 'Back',
    genericError: 'Something went wrong.',
    needSignIn: 'You need to be signed in to join this call.',
    joinError: 'Could not join the call.',
  },
  legal: {
    backHome: '← Home',
    termsLink: 'Terms of Use',
    privacyLink: 'Privacy Policy',
  },
  payments: {
    successTitle: 'Payment authorized',
    successBodyPrefix: 'Thanks — your payment for this session',
    successBodyBooking: '(booking {{id}})',
    successBodySuffix: "is authorized and held. You won't be charged unless the therapist accepts; if they don't respond within 72 hours, the hold is released automatically and you're not charged.",
    backHome: 'Back home',
    cancelledTitle: 'Payment not completed',
    cancelledBody: "Your booking is still reserved, but payment wasn't completed. You can try again from your bookings list.",
  },
  common: {
    loading: 'Loading…',
    cancel: 'Cancel',
  },
  twoFactor: {
    title: 'Two-step verification',
    intro: 'A second step at sign-in, so your password alone is not enough for someone to get into your account.',
    off: 'Currently off.',
    onTotp: 'On — via an authenticator app.',
    onEmail: 'On — with a code sent to your email.',
    turnOn: 'Turn on',
    turnOff: 'Turn off',
    totpTitle: 'Authenticator app',
    totpBody: 'Stronger. You need an app such as Google Authenticator on your phone.',
    chooseTotp: 'Use an app',
    emailTitle: 'Code by email',
    emailBody: 'Simpler. We send a six-digit code each time you sign in. No app needed.',
    chooseEmail: 'Use email',
    scanHint: 'Scan the QR code with your app, then type the six-digit code it shows.',
    qrAlt: 'QR code for your authenticator app',
    manualKey: 'Or enter the key manually',
    emailSent: 'We sent a six-digit code to {{email}}.',
    resend: 'Send again',
    resent: 'A new code was sent to {{email}}.',
    codeLabel: 'Code',
    confirm: 'Confirm',
    recoveryTitle: 'Recovery codes',
    recoveryBody: 'Keep these somewhere safe. They are shown only now and we cannot show them again. Without them, losing your phone or your mailbox means losing the account.',
    savedThem: "I've saved them",
    disableHint: 'For safety this needs both your password and a current two-step code.',
    passwordLabel: 'Password',
    disabled: 'Two-step verification is off.',
    genericError: 'Something went wrong. Try again.',
  },
};
